import React, { useEffect, useState } from "react";
import { 
    PawPrint, Calendar, Scale, AlertTriangle, Layers, 
    Heart, ShieldAlert, Award, Activity, Sparkles, Utensils, Bone,
    Scissors, ShieldCheck, ArrowLeft, Zap, Info
} from "lucide-react";
import { useLazyGetPetProfileByIdQuery } from "../../redux/actions/Customer/profileCustomer";
import { PetData } from "../../types/Response/CustomerResponse";
import { getPetTypeString, getGenderString } from "./AddPetProfileUI"; 

const calculateAge = (dateString: string | undefined): string => {
    if (!dateString) return "Unknown age";
    const birthDate = new Date(dateString);
    const today = new Date();
    
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    
    if (months < 0) {
        years--;
        months += 12;
    }
    
    if (years === 0) {
        return `${months} ${months === 1 ? "month" : "months"}`;
    }
    return `${years} ${years === 1 ? "year" : "years"} ${months > 0 ? `, ${months} ${months === 1 ? "mo" : "mos"}` : ""}`;
};

type ViewPetProfileProps = {
    petId: number;
    onBackClick?: () => void;
};

const ViewPetProfileUI: React.FC<ViewPetProfileProps> = ({ petId, onBackClick }) => {
    const [fetchPetProfile, { isFetching: isLoading }] = useLazyGetPetProfileByIdQuery();
    const [pet, setPet] = useState<PetData | null>(null);

    useEffect(() => {
        const loadProfileData = async () => {
            try {
                const response = await fetchPetProfile(petId).unwrap();
                if (response?.data) {
                    setPet(response.data);
                }
            } catch (err) {
                console.error("Failed to load pet view data stream metrics:", err);
            }
        };
        loadProfileData();
    }, [petId]);

    if (isLoading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center space-y-4">
                <div className="relative w-16 h-16">
                    <div className="absolute inset-0 rounded-full border-4 border-gray-100"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-[#2F5E2E] border-t-transparent animate-spin"></div>
                </div>
                <p className="text-[#2F5E2E] font-semibold text-sm tracking-wide animate-pulse">
                    Opening Pet Passport...
                </p>
            </div>
        );
    }

    if (!pet) {
        return (
            <div className="p-8 text-center bg-white rounded-3xl border border-gray-100 max-w-xl mx-auto mt-12 shadow-sm">
                <ShieldAlert className="text-red-400 mx-auto mb-3" size={40} />
                <p className="text-gray-700 font-semibold">Pet profile could not be located.</p>
                {onBackClick && (
                    <button onClick={onBackClick} className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-xs bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition">
                        <ArrowLeft size={14} /> Return to Profile
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="w-full max-w-5xl mx-auto animate-fadeIn space-y-6">
            
            {/* HERO IDENTITY BANNER HEAD */}
            <div className="relative bg-gradient-to-br from-[#2F5E2E] via-[#3d743c] to-[#1e3d1d] rounded-3xl shadow-xl overflow-hidden p-6 sm:p-8 text-white">
                {/* Decorative Pattern Background Layer */}
                <div className="absolute right-0 bottom-0 opacity-[0.06] pointer-events-none transform translate-x-12 translate-y-12 select-none">
                    <PawPrint size={280} />
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-6 relative z-10">
                    {/* Immersive Profile Image Ring */}
                    <div className="relative group w-32 h-32 rounded-2xl bg-white/10 backdrop-blur-md p-1 border border-white/20 shadow-xl flex-shrink-0">
                        {pet.photoUrl ? (
                            <img src={pet.photoUrl} alt={pet.petName} className="w-full h-full object-cover rounded-xl shadow-inner" />
                        ) : (
                            <div className="w-full h-full rounded-xl bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center">
                                <PawPrint size={52} className="text-white/70" />
                            </div>
                        )}
                    </div>

                    {/* Meta Names Content Block */}
                    <div className="text-center sm:text-left flex-1 space-y-2">
                        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2.5">
                            <h1 className="text-3xl font-extrabold tracking-tight drop-shadow-xs">{pet.petName}</h1>
                            <span className="px-3 py-0.5 rounded-full text-[10px] uppercase font-black tracking-wider bg-white/20 text-white border border-white/20 backdrop-blur-md">
                                {getPetTypeString(pet.petType)}
                            </span>
                        </div>
                        <p className="text-md text-emerald-100/90 font-medium capitalize tracking-wide">
                            {pet.breed || "Purebred Mix"} <span className="mx-1 text-white/40">•</span> {getGenderString(pet.gender)}
                        </p>
                        
                        {/* Dynamic Computed Timeline Badge */}
                        <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-amber-300 text-amber-950 px-3.5 py-1 rounded-xl text-xs font-black shadow-md">
                            <Calendar size={13} className="text-amber-900" />
                            Age: {calculateAge(pet.petDate)}
                        </div>
                    </div>

                    
                </div>
            </div>

            {/* INTEGRATED PASSPORT DASHBOARD MATRIX TILES GRID */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* COLUMN ONE: CORE ANTHROPOMETRIC SPECS */}
                <div className="md:col-span-1 space-y-6">
                    <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-xs space-y-5">
                        <h3 className="text-xs font-black uppercase text-gray-400 tracking-wider flex items-center gap-2">
                            <Activity size={15} className="text-[#2F5E2E]" /> Vitals & Metrics
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-3.5">
                            <div className="bg-gray-50/70 p-3.5 rounded-2xl border border-gray-100/60 flex flex-col justify-between">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Weight</p>
                                <p className="text-xl font-black text-gray-800 flex items-baseline gap-0.5 mt-1.5">
                                    {pet.weight || "0"} <span className="text-xs font-bold text-gray-500">KG</span>
                                </p>
                            </div>
                            <div className="bg-gray-50/70 p-3.5 rounded-2xl border border-gray-100/60 flex flex-col justify-between">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Size Class</p>
                                <p className="text-base font-black text-gray-800 mt-1.5 flex items-center gap-1">
                                    <Layers size={14} className="text-emerald-600" /> {pet.breedSize || "Medium"}
                                </p>
                            </div>
                        </div>

                        <div className="bg-gray-50/70 p-4 rounded-2xl border border-gray-100/60 space-y-2">
                            <div className="flex justify-between items-center">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide flex items-center gap-1">
                                    <Zap size={12} className="text-amber-500 fill-amber-500" /> Energy Burn Index
                                </p>
                                <span className="text-xs font-extrabold text-gray-600">Level {pet.activityLevel || "2"}</span>
                            </div>
                            <div className="w-full bg-gray-200/80 h-2 rounded-full overflow-hidden p-0.5">
                                <div 
                                    className="bg-gradient-to-r from-[#A8D97B] to-[#2F5E2E] h-full rounded-full transition-all duration-500" 
                                    style={{ width: `${(Number(pet.activityLevel || 2) / 3) * 100}%` }}
                                />
                            </div>
                        </div>

                        {pet.furColor && (
                            <div className="bg-gray-50/70 p-3.5 rounded-2xl border border-gray-100/60 flex items-center justify-between">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Fur Coat Color</p>
                                <p className="text-xs font-extrabold text-gray-700 bg-white px-2.5 py-1 rounded-lg border border-gray-100 shadow-xs capitalize">
                                    {pet.furColor}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* PERSONALITY CHIP WRAPPERS */}
                    {pet.personality && pet.personality.length > 0 && (
                        <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-xs space-y-4">
                            <h3 className="text-xs font-black uppercase text-gray-400 tracking-wider flex items-center gap-2">
                                <Sparkles size={15} className="text-[#2F5E2E]" /> Temperament Traits
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {pet.personality.map((trait, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#A8D97B]/10 text-[#2F5E2E] border border-[#A8D97B]/20 rounded-xl transition hover:bg-[#A8D97B]/20 capitalize">
                                        <Info size={10} className="opacity-60" /> {trait}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* COLUMN TWO & THREE: NUTRITION, HYGIENE, & HEALTH PASSPORT PANELS */}
                <div className="md:col-span-2 space-y-6">
                    
                    {/* NUTRITION & FEEDING CALIBRATION BLOCKS */}
                    <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-xs space-y-5">
                        <h3 className="text-xs font-black uppercase text-gray-400 tracking-wider flex items-center gap-2">
                            <Utensils size={15} className="text-[#2F5E2E]" /> Dietary & Menu Preferences
                        </h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5 bg-gray-50/50 p-3 rounded-2xl border border-gray-100/60">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Daily Feeding Target</p>
                                <div className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#6DBE45]"></span>
                                    {pet.dietFrequency === "1" && "Once Daily Schedule"}
                                    {pet.dietFrequency === "2" && "Twice Daily Meals (Standard)"}
                                    {pet.dietFrequency === "3" && "Three Times Daily"}
                                    {!["1","2","3"].includes(pet.dietFrequency || "") && (pet.dietFrequency || "Custom Timing")}
                                </div>
                            </div>
                            <div className="space-y-1.5 bg-gray-50/50 p-3 rounded-2xl border border-gray-100/60">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Calibrated Scoop Size</p>
                                <div className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#6DBE45]"></span>
                                    {pet.portionSize === "1" && "Light Serving Control"}
                                    {pet.portionSize === "2" && "Medium Regular Portion"}
                                    {pet.portionSize === "3" && "Generous Intake Serving"}
                                    {!["1","2","3"].includes(pet.portionSize || "") && (pet.portionSize || "Standard Balance")}
                                </div>
                            </div>
                        </div>

                        {pet.foodPreferences && pet.foodPreferences.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Diet Style Focus</p>
                                <div className="flex flex-wrap gap-2">
                                    {pet.foodPreferences.map((food, idx) => (
                                        <span key={idx} className="px-3 py-1.5 bg-amber-50 text-amber-800 border border-amber-100 text-xs font-bold rounded-xl flex items-center gap-1.5">
                                            <span>🍗</span> {food}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {pet.favouriteTreats && pet.favouriteTreats.length > 0 && (
                            <div className="space-y-2 border-t border-gray-50 pt-3">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide flex items-center gap-1">
                                    <Bone size={12} className="text-orange-500" /> Favorite Treats & Rewards
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {pet.favouriteTreats.map((treat, idx) => (
                                        <span key={idx} className="px-3 py-1.5 bg-orange-50/60 border border-orange-100/70 text-orange-800 font-bold rounded-xl text-xs flex items-center gap-1.5">
                                            <span>🍪</span> {treat}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* VETERINARY CLINICAL CONDITIONS MONITOR */}
                    <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-xs space-y-4">
                        <h3 className="text-xs font-black uppercase text-gray-400 tracking-wider flex items-center gap-2">
                            <Heart size={15} className="text-red-500" /> Medical Log Passport
                        </h3>

                        {pet.noKnownIssues ? (
                            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 text-emerald-950 p-4 rounded-2xl flex items-center gap-3.5 shadow-xs">
                                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 flex-shrink-0">
                                    <ShieldCheck size={20} />
                                </div>
                                <div className="text-xs">
                                    <p className="font-extrabold text-emerald-900">Condition Declaration Clear</p>
                                    <p className="text-emerald-800/70 mt-0.5">No biological food allergies, issues, or medical histories logged on file.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {pet.healthConditions && pet.healthConditions.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {pet.healthConditions.map((condition, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-red-50 text-red-800 border border-red-100 text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-2xs">
                                                <AlertTriangle size={12} className="text-red-500" /> {condition}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs italic text-gray-400">No symptomatic issues currently registered.</p>
                                )}

                                {pet.requiresVetConsultationAlert && (
                                    <div className="bg-gradient-to-br from-amber-400/10 to-orange-400/5 border border-amber-200 text-amber-950 p-4 rounded-2xl flex items-start gap-3">
                                        <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
                                        <div className="text-xs">
                                            <p className="font-black text-amber-900">Veterinary Food Sensitivity Alert Triggered</p>
                                            <p className="text-gray-600 mt-1 leading-relaxed">
                                                This profile registers specific ingredients requiring close observation. Check with your vet before updating dietary parameters.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* COAT CARE HYGIENE INTELLIGENCE PANEL */}
                    <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-xs grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-2 bg-gray-50/40 p-3.5 rounded-2xl border border-gray-100/60 flex items-start gap-3">
                            <div className="w-8 h-8 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600 flex-shrink-0 mt-0.5">
                                <Scissors size={15} />
                            </div>
                            <div className="space-y-0.5">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Grooming Routine</p>
                                <p className="text-sm font-black text-gray-700 capitalize">
                                    {pet.groomingFrequency === "1" && "Weekly Care"}
                                    {pet.groomingFrequency === "2" && "Monthly Interval"}
                                    {pet.groomingFrequency?.startsWith("Custom:") ? pet.groomingFrequency : (pet.groomingFrequency === "3" ? "Custom Schedule" : pet.groomingFrequency)}
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2 bg-gray-50/40 p-3.5 rounded-2xl border border-gray-100/60 flex items-start gap-3">
                            <div className="w-8 h-8 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-600 flex-shrink-0 mt-0.5">
                                <Award size={15} />
                            </div>
                            <div className="space-y-0.5">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Shedding Volume</p>
                                <p className="text-sm font-black text-gray-700 capitalize">
                                    {pet.sheddingFrequency === "1" && "Low Shedder"}
                                    {pet.sheddingFrequency === "2" && "Seasonal Balance"}
                                    {pet.sheddingFrequency === "3" && "Heavy Shedding Coat"}
                                    {!["1","2","3"].includes(pet.sheddingFrequency || "") && (pet.sheddingFrequency || "Standard Level")}
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ViewPetProfileUI;